import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LucideAngularModule, Globe, Check } from 'lucide-angular';
import { NotificationService } from 'src/app/core/services/notification.service';
import {
  CompanySdgsService,
  MAX_SELECTED_SDGS,
  REQUIRED_SDG_ID,
} from 'src/app/shared/services/company-sdgs.service';
import { SDG_DATA } from '../sdg-data';

/** SDG selection and its read-only inspiration lists. Unrelated to reduction tips. */
@Component({
  selector: 'app-sdg-picker',
  standalone: true,
  imports: [TranslocoModule, LucideAngularModule],
  templateUrl: './sdg-picker.component.html',
})
export class SdgPickerComponent {
  private companySdgsService = inject(CompanySdgsService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);

  readonly SDG_DATA = SDG_DATA;
  readonly SDG13_ID = REQUIRED_SDG_ID;
  readonly MAX_SDGS = MAX_SELECTED_SDGS;
  readonly Globe = Globe;
  readonly Check = Check;

  activeLang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  selectedSdgs = this.companySdgsService.selectedSdgs;
  isSaving = signal(false);

  selectionCount = computed(() => this.selectedSdgs().length);
  selectedSdgData = computed(() =>
    SDG_DATA.filter((sdg) => this.selectedSdgs().includes(sdg.id)),
  );

  getSdgTips(sdgId: number): string[] {
    const tips = this.translocoService.translate<string[]>(`objectives.sdgTips.${sdgId}`);
    return Array.isArray(tips) ? tips : [];
  }

  isSelected(sdgId: number): boolean {
    return this.selectedSdgs().includes(sdgId);
  }

  isLocked(sdgId: number): boolean {
    return sdgId === this.SDG13_ID;
  }

  toggleSdg(sdgId: number): void {
    if (this.isLocked(sdgId) || this.isSaving()) return;

    const current = this.selectedSdgs();
    if (current.includes(sdgId)) {
      this.save(current.filter((id) => id !== sdgId));
      return;
    }

    if (current.length >= this.MAX_SDGS) {
      this.notificationService.warn(
        this.translocoService.translate('objectives.sdgMaxWarning'),
      );
      return;
    }

    this.save([...current, sdgId]);
  }

  private save(selectedSdgs: number[]): void {
    this.isSaving.set(true);
    this.companySdgsService.updateSdgs(selectedSdgs).subscribe({
      next: () => this.isSaving.set(false),
      error: () => {
        this.isSaving.set(false);
        this.notificationService.error(
          this.translocoService.translate('objectives.sdgSaveError'),
        );
      },
    });
  }
}
